import { AppConstants } from 'cd-eft/appConstants';
import { action, observable } from 'mobx';
import { GraphicsLayer, MapView } from 'cd-eft/utils/esriTypes';
import stores from 'cd-eft/models/Stores';
import { displaySurveyPointsClientSide } from '../Maps/SETMapFunctions/loadLayers';
import { appConfig } from 'cd-eft/config/AppConfig';
import { SetSurveyController } from 'cd-eft/services/eftSetApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

const setStore = stores.AppStore.surveyEngineeringStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;

export class PointTranslationVM {
  private _currentWkid = appConfig.tempHardCodes.wkid;
  @observable shouldDisplayOnMap = false;
  @observable props;
  constructor(props: any) {
    this.props = props;
    if (setStore.translationOptionApplied !== 0) {
      this.translationOption = setStore.translationOptionApplied;
    }
  }

  renderer = {
    type: 'simple',
    symbol: {
      type: 'simple-marker',
      color: 'blue',
      size: '4',
      outline: null,
    },
  };

  @observable translationOption: number = 1;
  @observable desirableOption: number = 0;
  @observable pointTranslationDialogIsOpen: boolean = false;

  // if no survey selected and displayed from summary, load all xyz surveys
  @action.bound async displayAllXYZSurveys() {
    setStore.isLoading = true;
    if (gisModel.map) {
      const xyzSurveys = setStore.surveySummaryTable.filter(
        (sur) => sur.surveyType === appConfig.hardCodes.surveyTypeXYZ
      );
      for (const survey of xyzSurveys) {
        setStore.isLoading = true;
        try {
          const response: any = await SetSurveyController.getSurveyUsingGET(survey.surveyId);
          // console.log('getSurveyPointsResponse:', response);
          displaySurveyPointsClientSide(
            gisModel.view,
            response.data.xyzArray,
            survey.surveyId,
            survey.surveyName,
            true,
            this.renderer,
            this._currentWkid
          );
          setStore.selectedSurveysInfo.push(survey.surveyId);
          setStore.pointsOnMap = true;
        } catch (error) {
          MessageCenter.addError(
            (error as any).response && (error as any).response.data && (error as any).response.data.errorMessage
          );
        }
      }
    }
    setStore.isLoading = false;
  }

  // select Translation Option (open confirmation Popup if current translation not saved)
  @action.bound selectTranslateOptionHandler(e: any) {
    this.formError = '';
    if (setStore.translationOptionApplied !== 0) {
      this.desirableOption = +e.target.value;
      this.pointTranslationDialogIsOpen = true;
    } else {
      this.translationOption = +e.target.value;
      for (const vm of [setStore.transSketchVM, setStore.rotateSketchVM]) {
        vm?.layer.removeAll();
      }
    }
  }

  // Popup window: revert to original points and go to desirable option
  @action.bound fromPopupRevertToOriginal() {
    this.cancelPointTranslation();
    this.translationOption = this.desirableOption;
    this.desirableOption = 0;
    this.pointTranslationDialogIsOpen = false;
  }

  // Popup window: keep translation and stay on current option
  @action.bound fromPopupKeepTranslation() {
    this.desirableOption = 0;
    this.pointTranslationDialogIsOpen = false;
  }

  // Go to new survey
  @action.bound acceptTranslation() {
    setStore.changeTab('New Survey');
  }

  // cancel points translation and refresh map
  @action.bound cancelPointTranslation() {
    setStore.resetTranslataion();
    this.refreshNewSurveyPointsDisplay();
    if (gisModel.view) {
      clearPairSelectLines(gisModel.view);
    }
    setStore.translationOptionApplied = 0;
  }

  // Point translation window: Cancel button: cancel translation and go to new survey
  @action.bound onCancelPointTranslation() {
    this.cancelPointTranslation();
    setStore.changeTab('New Survey');
  }

  // Point translation window: Accept button: accept translation and go to new survey
  @action.bound applyPointsTranslation() {
    setStore.translationOptionApplied = this.translationOption;
    this.pointTranslationCalculations();
  }

  // Point translation window: Revert button: revert to original points and stay on current page
  @action.bound returnToOriginalPoints(e) {
    e.preventDefault();
    setStore.resetTranslataion();
    this.refreshNewSurveyPointsDisplay();
    if (gisModel.view) {
      clearPairSelectLines(gisModel.view);
    }
    setStore.translationOptionApplied = 0;
  }

  // point Translation Calculations
  @action.bound pointTranslationCalculations() {
    // after translation make selected points not tinnable
    // option 2.
    if (setStore.transRefName) {
      const transRefIndex = setStore.xyzPointsTable.findIndex(
        (el) =>
          String(el.pointName) === String(setStore.transRefName) &&
          el.x === setStore.transRefX &&
          el.y === setStore.transRefY &&
          el.z === setStore.transRefZ
      );
      if (transRefIndex !== -1) {
        setStore.xyzPointsTable[transRefIndex].tinable = 0;
        setStore.xyzPointsTable[transRefIndex].visible = 0;
        setStore.xyzPointsTable[transRefIndex].hasDuplicate = 1;
      }
    }
    // option 3.
    if (setStore.pivotRefName) {
      const pivotRefIndex = setStore.xyzPointsTable.findIndex(
        (el) =>
          String(el.pointName) === String(setStore.pivotRefName) &&
          el.x === setStore.pivotRefX &&
          el.y === setStore.pivotRefY &&
          el.z === setStore.pivotRefZ
      );
      if (pivotRefIndex !== -1) {
        setStore.xyzPointsTable[pivotRefIndex].tinable = 0;
        setStore.xyzPointsTable[pivotRefIndex].visible = 0;
        setStore.xyzPointsTable[pivotRefIndex].hasDuplicate = 1;
      }
    }
    if (setStore.angleRefName) {
      const angleRefIndex = setStore.xyzPointsTable.findIndex(
        (el) =>
          String(el.pointName) === String(setStore.angleRefName) &&
          el.x === setStore.angleRefX &&
          el.y === setStore.angleRefY
      );
      if (angleRefIndex !== 1) {
        setStore.xyzPointsTable[angleRefIndex].tinable = 0;
        setStore.xyzPointsTable[angleRefIndex].visible = 0;
        setStore.xyzPointsTable[angleRefIndex].hasDuplicate = 1;
      }
    }
    //

    if (setStore.rotationAngle && setStore.pivotRefX && setStore.pivotRefY) {
      setStore.xyzPointsTable.forEach((e, i) => {
        // compute transform coefficients
        let cosTheta = 0.0;
        let sinTheta = 0.0;
        let kx = 0.0;
        let ky = 0.0;

        // compute rotation angle in std trig radians
        const theta = 0.0 - (setStore.rotationAngle * Math.PI) / 180.0;

        // pre-compute constants, to minimize transcendental math
        cosTheta = Math.cos(theta);
        sinTheta = Math.sin(theta);
        kx = setStore.pivotRefX + setStore.pivotRefY * sinTheta - setStore.pivotRefX * cosTheta;
        ky = setStore.pivotRefY - setStore.pivotRefX * sinTheta - setStore.pivotRefY * cosTheta;

        // transform each point
        const deltaX = Number(setStore.pivotDeltaX);
        const deltaY = Number(setStore.pivotDeltaY);
        const deltaZ = Number(setStore.pivotDeltaZ);

        const newX = e.x * cosTheta - e.y * sinTheta + kx;
        const newY = e.x * sinTheta + e.y * cosTheta + ky;

        setStore.xyzPointsTable[i].x = newX + deltaX;
        setStore.xyzPointsTable[i].y = newY + deltaY;
        setStore.xyzPointsTable[i].z = e.z + deltaZ;
      });
    } else {
      setStore.xyzPointsTable.forEach((e, i) => {
        setStore.xyzPointsTable[i].x = Number(e.x) + Number(setStore.deltaX);
        setStore.xyzPointsTable[i].y = Number(e.y) + Number(setStore.deltaY);
        setStore.xyzPointsTable[i].z = Number(e.z) + Number(setStore.deltaZ);
      });
    }

    this.refreshNewSurveyPointsDisplay();
    if (gisModel.view) clearPairSelectLines(gisModel.view, true);
  }

  private refreshNewSurveyPointsDisplay() {
    const layerId = 'new_survey';
    const map = gisModel.map;
    let newSurveyLayer;
    if (map) {
      for (const lyr of map.layers) {
        if (lyr.id === layerId) {
          newSurveyLayer = lyr;
          break;
        }
      }
    }

    if (newSurveyLayer) {
      map.remove(newSurveyLayer);
      const survey1 = setStore.xyzPointsTable;
      const renderer = newSurveyLayer.renderer;
      displaySurveyPointsClientSide(
        gisModel.view,
        survey1,
        layerId,
        setStore.currentSurvey.surveyName,
        true,
        renderer,
        this._currentWkid
      );
    }
  }

  @action.bound handleSubmit(e) {
    e.preventDefault();
    this.validateForm();
    if (this.formValid && setStore.translationOptionApplied === 0) {
      this.applyPointsTranslation();
    }
  }

  @action.bound closeDialog() {
    this.pointTranslationDialogIsOpen = false;
  }

  // ----------------------------- form validation ->
  @observable formValid: boolean = false;
  @observable formError: string = '';

  @action.bound setFormError(value) {
    this.formError = value;
  }

  @action.bound async validateForm() {
    switch (this.translationOption) {
      case 1:
        if (setStore.deltaX === '') setStore.deltaX = +0;
        if (setStore.deltaY === '') setStore.deltaY = +0;
        if (setStore.deltaZ === '') setStore.deltaZ = +0;
        if (setStore.deltaX !== 0 || setStore.deltaY !== 0 || setStore.deltaZ !== 0) {
          this.formError = '';
          this.formValid = true;
        } else {
          const err = await MessageCenter.addErrorLabelFromCode('Survey_10');
          this.formError = err; // 'Please fill at least one field';
        }
        break;
      case 2:
        if (setStore.transRefName !== '' && setStore.transTargetName !== '') {
          this.formError = '';
          this.formValid = true;
        } else {
          const err = await MessageCenter.addErrorLabelFromCode('Survey_11');
          this.formError = err; // 'Please select Ref. Pt and Target Point';
        }
        break;
      case 3:
        if (
          setStore.pivotRefName !== '' &&
          setStore.pivotTargetName !== '' &&
          setStore.angleRefName !== '' &&
          setStore.angleTargetName !== ''
        ) {
          this.formError = '';
          this.formValid = true;
        } else {
          const err = await MessageCenter.addErrorLabelFromCode('Survey_12');
          this.formError = err; // 'Please select all Ref. and Target Points';
        }
        break;
      default:
        break;
    }
  }

  // <- -------------------------- form validation
}

// reusable function
function clearPairSelectLines(view: MapView, keepHighlight = false) {
  const map = view.map;
  for (const lyrId of [AppConstants.transSketchLayerId, AppConstants.rotateSketchLayerId]) {
    const lyr = map.findLayerById(lyrId) as GraphicsLayer;
    if (lyr) {
      if (keepHighlight) {
        for (const graphic of lyr.graphics) {
          if (graphic.geometry.type !== 'point') {
            lyr.graphics.remove(graphic);
          }
        }
      } else {
        lyr.removeAll();
      }
    }
  }
}
